// A source focused in the right panel (right panel v2), as pure projections:
// where it is used (statements per material that its claims back, chat
// replies), the statement the author arrived from, which of its passages are
// quoted, and how a long "used in" list folds. The view (SourceFocus.tsx)
// only renders these.
//
// Quotes are matched loosely (case and whitespace insensitive): a claim's
// quote is verbatim source text, but extraction may have re-flowed its spaces.
//
// Pure: total folds over values. No I/O, no casts.

import type {
    MaterialRef,
    SourceRef,
} from "../../../components/Navigation/types.js";
import {
    type StatementSource,
    statementSourcesOf,
} from "../../../components/QualitySummary/QualitySummary.js";
import type {
    SourceBlock,
    SourceProjection,
} from "../../../components/SourceView/types.js";
import type { StatementOrigin } from "../../frame/types.js";
import { type ChatWorld, homeOf, type SourceUsage, usageOf } from "./home.js";

// ── Usage ───────────────────────────────────────────────────────────────────

// Where a source is used, also for a source the chat never touched itself
// (opened from a material's citation, or from "other files").
export const usageIn = (world: ChatWorld, ref: SourceRef): SourceUsage =>
    usageOf(
        world,
        world.sources.find((s) => s.ref.id === ref.id) ?? {
            ref,
            arrival: "read",
        },
    );

// Whether the source sits outside the chat's sources (group 3, or never in the
// chat at all): then the panel offers to attach it.
export const canAttach = (world: ChatWorld, ref: SourceRef): boolean => {
    const home = homeOf(world);
    return (
        home.type === "empty" ||
        !home.sources.some((u) => u.source.ref.id === ref.id)
    );
};

// The claim the author came from, resolved against the live quality read
// (undefined once the citation is gone).
export type Arrival = {
    readonly material: MaterialRef;
    readonly row: StatementSource;
};

export const arrivalOf = (
    world: ChatWorld,
    source: SourceRef,
    origin: StatementOrigin | undefined,
): Arrival | undefined => {
    if (origin === undefined) return undefined;
    const material = world.materials.find(
        (m) => m.ref.id === origin.material.id,
    );
    const row = statementSourcesOf(material?.quality ?? null).find(
        (r) =>
            r.statementId === origin.statementId && r.source.id === source.id,
    );
    return row === undefined ? undefined : { material: origin.material, row };
};

// ── Long lists fold per material ────────────────────────────────────────────

const STATEMENTS_SHOWN = 5;

export type FoldedStatements = {
    readonly shown: readonly StatementSource[];
    // How many more a "Show all" would reveal (0 = nothing folded).
    readonly hidden: number;
};

export const foldStatements = (
    rows: readonly StatementSource[],
    expanded: boolean,
): FoldedStatements =>
    expanded || rows.length <= STATEMENTS_SHOWN + 1
        ? { shown: rows, hidden: 0 }
        : {
              shown: rows.slice(0, STATEMENTS_SHOWN),
              hidden: rows.length - STATEMENTS_SHOWN,
          };

// ── Quotes in the text ──────────────────────────────────────────────────────

const loose = (text: string): string =>
    text.replace(/\s+/g, " ").trim().toLowerCase();

const holds = (text: string, quote: string): boolean => {
    const q = loose(quote);
    return q !== "" && loose(text).includes(q);
};

// Every quote the chat and its materials take from this source.
export const quotesOf = (usage: SourceUsage): readonly string[] => [
    ...usage.inMaterials.flatMap((use) => use.rows.map((r) => r.quote)),
    ...usage.inChat.flatMap((r) =>
        r.citation.quote === undefined ? [] : [r.citation.quote],
    ),
];

const markBlocks = (
    blocks: readonly SourceBlock[],
    quotes: readonly string[],
): readonly SourceBlock[] =>
    blocks.map(
        (block): SourceBlock =>
            quotes.some((q) => holds(block.text, q))
                ? { type: "highlighted", id: block.id, text: block.text }
                : block,
    );

// The source's readable text with every quoted passage highlighted (the reader
// marks highlighted blocks, D19). Kinds without text blocks pass through.
export const markQuotes = (
    content: SourceProjection,
    quotes: readonly string[],
): SourceProjection => {
    switch (content.type) {
        case "text":
            return { ...content, blocks: markBlocks(content.blocks, quotes) };
        case "pdf":
            return { ...content, blocks: markBlocks(content.blocks, quotes) };
        case "word":
            return { ...content, blocks: markBlocks(content.blocks, quotes) };
        case "video":
        case "image":
        case "media":
        case "unavailable":
            return content;
    }
};

// The passage (block) a quote lands in, for the arrival card.
export const passageOf = (
    content: SourceProjection,
    quote: string,
): string | undefined => {
    switch (content.type) {
        case "text":
        case "pdf":
            return content.blocks.find((b) => holds(b.text, quote))?.text;
        case "video":
        case "image":
        case "media":
        case "unavailable":
            return undefined;
    }
};

// A passage cut into plain and quoted runs, so the exact quote is marked.
export type Run = {
    readonly type: "plain" | "quoted";
    readonly text: string;
};

const escapeRegExp = (s: string): string =>
    s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const runsOf = (text: string, quote: string): readonly Run[] => {
    const words = quote
        .trim()
        .split(/\s+/)
        .filter((w) => w !== "");
    const hit =
        words.length === 0
            ? null
            : new RegExp(words.map(escapeRegExp).join("\\s+"), "i").exec(text);
    if (hit === null) return [{ type: "plain", text }];
    const end = hit.index + hit[0].length;
    const runs: readonly Run[] = [
        { type: "plain", text: text.slice(0, hit.index) },
        { type: "quoted", text: hit[0] },
        { type: "plain", text: text.slice(end) },
    ];
    return runs.filter((run) => run.text !== "");
};
